export type ProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
  request_id?: string;
  errors?: unknown;
};

export type ErrorDetail = string | number | boolean | null;

export type ErrorDetails = Readonly<Record<string, ErrorDetail>>;

const isDetail = (value: unknown): value is ErrorDetail =>
  value === null || ['string', 'number', 'boolean'].includes(typeof value);

const detailsOf = (errors: unknown): ErrorDetails =>
  typeof errors === 'object' && errors !== null && !Array.isArray(errors)
    ? Object.fromEntries(Object.entries(errors).filter(([, value]) => isDetail(value)))
    : {};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail: string | null;
  readonly requestId: string | null;
  readonly details: ErrorDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `HTTP ${String(status)}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = problem.code ?? `HTTP_${String(status)}`;
    this.title = problem.title ?? '';
    this.detail = problem.detail ?? null;
    this.requestId = problem.request_id ?? null;
    this.details = detailsOf(problem.errors);
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isRetryable(): boolean {
    return this.status === 429 || this.status >= 500;
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Network request failed');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}
